import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";
import { api, fmtErr } from "@/api";
import { ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError(t("rp_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-[#E5E2D9] bg-[#FDFCF8] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200";
  const labelCls = "block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C] mb-2";

  return (
    <div className="min-h-screen bg-[#F3F1EB] flex items-center justify-center px-6 py-16" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-10" data-testid="reset-home-link">
          <Logo size={44} />
        </Link>
        <div className="bg-white border border-[#E5E2D9] p-8 sm:p-10 shadow-[8px_8px_0_0_#E5E2D9]">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-16 h-16 mx-auto bg-[#F7F0D8] flex items-center justify-center mb-6">
                <ShieldCheck size={28} className="text-[#B5952F]" />
              </div>
              <p className="text-sm text-[#1A1A1A] font-semibold leading-relaxed">{t("rp_done")}</p>
              <Link
                to="/login"
                className="inline-block mt-8 bg-[#1A1A1A] text-[#D4AF37] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300"
                data-testid="reset-goto-login-button"
              >
                {t("nav_login")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-[#1A1A1A]">{t("rp_title")}</h1>
              <p className="text-sm text-[#5C5C5C] mt-2 mb-8">{t("rp_sub")}</p>
              {error && (
                <div className="mb-6 border border-[#E03131] bg-[#E03131]/5 text-[#E03131] text-sm font-semibold px-4 py-3" data-testid="reset-error">
                  {error}
                </div>
              )}
              {!token ? (
                <div className="border border-[#E03131] bg-[#E03131]/5 text-[#E03131] text-sm font-semibold px-4 py-3" data-testid="reset-no-token">
                  Invalid reset link. Please request a new one from the{" "}
                  <Link to="/forgot-password" className="underline font-bold">forgot password</Link> page.
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <label className={labelCls}>{t("rp_new")}</label>
                    <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="Min. 6 characters" data-testid="reset-password-input" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("rp_confirm")}</label>
                    <input type="password" required minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} placeholder="••••••••" data-testid="reset-confirm-input" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A1A1A] text-[#D4AF37] py-4 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-50"
                    data-testid="reset-submit-button"
                  >
                    {loading ? "..." : t("rp_btn")}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
